/**
 * components.module.css — the layout half of the media and chrome components.
 * `docs/design-direction.md` §6.4–6.5, §6.7, §8.2's flyer divider, §8.6's speaker
 * stack and §9.1–9.5.
 *
 * WHAT IS **NOT** HERE, ON PURPOSE
 *
 * The printed marks already exist as global recipes in `styles/texture.css`
 * (`.disc`, `.disc__label`, `.disc__spindle`, `.disc__rim`, `.plate__art`, `.wave`,
 * `.wave__unplayed`, `.wave__played`, `.wave__head`, `.ruler`, `.rule`,
 * `.paper__tape`, `.faceplate`, `.silkscreen`, `.halftone`), the two rotations in
 * `styles/motion.css`, the licensed mini-player shadow in `styles/tokens.css`
 * (`.miniplayer`) and the type tiers in `styles/type.css`. Every component below
 * attaches those class names verbatim and this module adds only what a *component*
 * legitimately owns: box model, hit slop, positioning, variants and the two
 * component-level animations that no primitive already declares.
 *
 * SIX RULES EVERY DECLARATION BELOW OBEYS
 *
 *  1. **Semantic aliases only** (§3.2). No `--ink-*`, `--paper-*`, `--stock-*`,
 *     `--warm-*` or `--cool` appears in this file. The `--plate-*` properties are not
 *     the raw palette: they are the per-track custom properties `plateCssVars()`
 *     writes onto one element, and they are the whole point of the plate system.
 *  2. **Radii are `var(--r-cut)` (0) or `var(--r-round)` (999px)** — nothing between
 *     (§5.3).
 *  3. **Separation is a 1px printed rule, never a shadow** (§5.5). The only
 *     `box-shadow` in this file is `--ink-offset`, which has a zero blur radius.
 *  4. **Every length that can be a token is a token.** Spacing comes from
 *     `--s1 --s2 --s3 --s4 --s6 --s8 --drop --bar`; `font-size` never appears at all,
 *     because type is set by the nine `.t-*` classes of `type.css` (§4.3) and a local
 *     `font-size` would also opt the text out of §4.4's font-failure degradation.
 *  5. **Green does not exist** (§3.1, §12.1) — no hue in 70–170° is reachable from
 *     this file.
 *  6. **Zero `mix-blend-mode` declarations.** The blend budget is three per screen
 *     (§6.13) and it is already spent by the grain layer, the misregistration ghost
 *     and the plate art duotone, all of which live in `texture.css`. A row in a
 *     virtualised ledger gets **none**, ever (§8.2, §9.3).
 *
 * `prefers-reduced-motion` is a first-class state (§7.4) handled globally by
 * `motion.css`, which forces `animation-duration: 1ms` and a single iteration on
 * every element. Every keyframe here is therefore written so the element's **resting
 * style is the finished style**: with motion reduced the label chip sits still at its
 * current angle, the loupe is simply at size, and nothing needs a `@media` block.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Shared: hit targets (§5.6), screen-reader copy, and the Cyrillic guard (§4.3).
   ──────────────────────────────────────────────────────────────────────────────── */

.hit {
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
}

.sr {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/*
 * Tracked uppercase Cyrillic is banned (§4.3, §12.16): `.t-11` ships +0.16em and
 * `.t-13` +0.10em, which on Russian caps reads as shouting. CSS cannot see the script
 * of a string, so the components detect it with `hasCyrillic` from `@bl/shared` and
 * add this class. It mirrors the primitives' guard rather than importing it, because
 * a CSS module class is not exported across files.
 */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   Disc — §6.4 / §7.2.
   `texture.css` owns the grooves, the 46% label and the spindle; `motion.css` owns
   the 1333ms revolution and the fact that a pause is `animation-play-state: paused`
   rather than `animation: none` — the record freezes mid-groove and never resets, so
   the scrawl sits at a different angle every time.
   The mount exists because a needle drop and a revolution are two animations on one
   element: the mount scales, the disc inside it spins, and the freeze angle survives.
   ──────────────────────────────────────────────────────────────────────────────── */

.mount {
  position: relative;
  display: inline-block;
  /* THE PULL UP (§7.3) is a drag on this element; with the reset's `manipulation`,
     Telegram's sheet-dismiss gesture steals it. */
  touch-action: none;
}

/* NEEDLE DROP (§7.2): scale .94 → 1 over `--tdrop`. One-shot, never on resume — the
   owner mounts the attribute for one track change and takes it off again. */
.mount[data-needle-drop='true'] {
  animation: needle-drop var(--tdrop) var(--ease-in) 1;
}

@keyframes needle-drop {
  from {
    transform: scale(0.94);
  }

  to {
    transform: scale(1);
  }
}

/* ────────────────────────────────────────────────────────────────────────────────
   RimText — §6.4. `SOUND SYSTEM · DUBPLATE SPECIAL · 45 RPM ·` on an SVG `textPath`,
   counter-rotating against the disc at 24s so the pressing-plant print stays roughly
   readable while the record turns. The 9px is set as an SVG attribute by the
   component and is deliberately off the nine-value scale for the reason `texture.css`
   records: it is rim print, not type.
   ──────────────────────────────────────────────────────────────────────────────── */

.rim {
  fill: var(--bg-paper-2);
  letter-spacing: 0.16em;
}

/* ────────────────────────────────────────────────────────────────────────────────
   PlateLabel — §6.5, the signature system.
   Three patterns, all of them **annular**: the ink prints as rings around a clear
   stock centre, which is where the catalogue line, the chinagraph scrawl and the
   artist sit. That is how a real 7" label is set, and it is also what keeps the type
   at `--text-on-paper` on `--bg-paper` (13.8:1) instead of stranding it on an ink
   whose contrast §12.9 forbids.
   ──────────────────────────────────────────────────────────────────────────────── */

.plate {
  display: grid;
  grid-template-rows: 1fr auto auto 1fr;
  justify-items: center;
  gap: var(--s1);
  padding: 0 var(--s4);
  overflow: hidden;
  color: var(--text-on-paper);
  text-align: center;
}

/*
 * The one thing in the app that is allowed to be centred is circular (§12.5): a
 * record label is set concentrically around the spindle, and so is its rim print.
 */

.ink {
  position: absolute;
  inset: 0;
  border-radius: var(--r-round);
  pointer-events: none;
}

/* Pattern 0 — a single ink ring at 62% diameter (the cheap one-plate cut). */
.plate[data-pattern='0'] .ink {
  inset: 19%;
  border: 3px solid var(--plate-ink-1);
}

/*
 * Pattern 1 — a hard conic split in the two inks, masked to the outer annulus so the
 * label centre stays stock. No blur, no easing between the halves: it is two plates
 * meeting on a straight edge.
 */
.plate[data-pattern='1'] .ink {
  background-image: conic-gradient(
    from 0deg,
    var(--plate-ink-1) 0deg 180deg,
    var(--plate-ink-2) 180deg 360deg
  );
  mask-image: radial-gradient(circle at 50% 50%, transparent 0 54%, #000 55% 100%);
  -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 0 54%, #000 55% 100%);
}

/* Pattern 2 — two concentric ink bands. */
.plate[data-pattern='2'] .ink {
  inset: 8%;
  border: 3px solid var(--plate-ink-1);
}

.plate[data-pattern='2'] .ink2 {
  inset: 22%;
  border: 2px solid var(--plate-ink-2);
  display: block;
}

.ink2 {
  display: none;
}

/*
 * Cover art, when the channel supplied one (§6.5). The duotone recipe is
 * `texture.css`'s `.plate__art`; what belongs to the component is the mask that keeps
 * the art in the same annulus the ink patterns use, so a bad 90s JPEG becomes the
 * label's printed ring rather than a photo with type dumped on top of it. This is the
 * line that converts the channel's worst asset into the app's most coherent one — and
 * it is never a full-bleed hero and never a grid (§12.22).
 */
:global(.plate__art).art {
  mask-image: radial-gradient(circle at 50% 50%, transparent 0 54%, #000 55% 100%);
  -webkit-mask-image: radial-gradient(circle at 50% 50%, transparent 0 54%, #000 55% 100%);
}

.cat {
  grid-row: 2;
  color: var(--text-on-paper);
}

/*
 * The chinagraph title. Caveat 600 at `--fs-20` — inside §6.5's 18–24px window and on
 * §4.3's closed scale, so it scales with Telegram's font setting instead of being
 * pinned to a literal. The tilt is `--plate-tilt`, written by `plateCssVars()` from
 * the same hash that chose the inks: the same track is scrawled at the same angle
 * forever.
 */
.scrawl {
  grid-row: 3;
  /* `.t-20` carries the size (§4.3's closed scale) but also Display's 800; the
     chinagraph hand is Caveat 600 (§4.1), and `font-synthesis: none` means a weight
     the face does not have is simply the nearest one it does. */
  font-weight: 600;
  letter-spacing: 0;
  max-inline-size: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  transform: rotate(var(--plate-tilt, -5deg));
  color: var(--text-on-paper);
}

.artist {
  grid-row: 4;
  align-self: start;
  max-inline-size: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--text-on-paper);
}

/* ────────────────────────────────────────────────────────────────────────────────
   LabelChip — §8.2 / §9.1. The procedural disc miniaturised to grooves plus two ink
   rings. It is instantly readable at 24px because it is *pattern*, not image, and it
   is the reason the crate needs no thumbnails and no cover-art wall (§12.22).
   ──────────────────────────────────────────────────────────────────────────────── */

.chip {
  position: relative;
  display: block;
  flex: none;
  border-radius: var(--r-round);
  background: repeating-radial-gradient(
    circle at 50% 50%,
    var(--bg) 0 1px,
    var(--bg-panel) 1px 1.5px,
    var(--bg) 1.5px 2.5px
  );
  box-shadow: var(--recess);
  animation: chip-spin 1333ms linear infinite;
}

/* 44px in the ledger, 24px in the mini player — both are spacing-scale values, which
   is the same coincidence that makes `--drop` the touch target and the rule width. */
.chip[data-size='44'] {
  inline-size: var(--drop);
  block-size: var(--drop);
}

.chip[data-size='24'] {
  inline-size: var(--s6);
  block-size: var(--s6);
}

.chip::before,
.chip::after {
  content: '';
  position: absolute;
  border-radius: var(--r-round);
}

.chip::before {
  inset: 18%;
  border: 2px solid var(--plate-ink-1);
}

.chip::after {
  inset: 34%;
  border: 1px solid var(--plate-ink-2);
}

/* 45 RPM, the same revolution as the dubplate (§7.2). The chip has no `translateX`
   to preserve, so its keyframe is a bare rotation. */
@keyframes chip-spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ────────────────────────────────────────────────────────────────────────────────
   Waveform — §6.7. REAL PEAKS ONLY. One `<path>` for the geometry and one `<use>`
   that re-paints it in the flare→amber gradient, clipped at the playhead: 512 rects
   would be 512 paint boxes on a phone and could not carry one vertical gradient.
   With no peaks there is no path at all — the designed state is the bar ruler and a
   3px progress bar, and a waveform synthesised from a hash is banned outright
   (§6.7, §12.6).
   ──────────────────────────────────────────────────────────────────────────────── */

.wave {
  display: block;
  inline-size: 100%;
}

/* The gradient stops: bass energy prints in hot ink at the axis, peaks reach amber.
   They are classes rather than attributes because `var()` is a CSS value and an SVG
   presentation *attribute* cannot resolve one. */
.stopAxis {
  stop-color: var(--meter-axis);
}

.stopPeak {
  stop-color: var(--meter-peak);
}

/* The playhead. A 2px halo and no blur is `texture.css`'s `.wave__head`; the position
   is this one declaration, written from `--played` on the same frame as `--rest`. */
.head {
  inset-inline-start: var(--played, 0%);
}

/* The no-peaks state: the ruler, a 3px `--accent` bar in a `--border-strong` track,
   and nothing else — in the same 56px band, so the scrub target never changes size
   when a track happens to have peaks and the next one does not. */
.bare {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--s2);
}

.track {
  display: block;
  inline-size: 100%;
  block-size: 3px;
  background: var(--border-strong);
}

.fill {
  display: block;
  block-size: 3px;
  background: var(--accent);
  transform-origin: left center;
  transform: scaleX(var(--wave-progress, 0));
}

/* ────────────────────────────────────────────────────────────────────────────────
   Scrub — §9.5. The 56px waveform band IS the control; the 1px playhead is never the
   target (§5.6). `touch-action: none` and `disableVerticalSwipes()` for the drag's
   duration, or Telegram's sheet-dismiss gesture steals it and the Mini App collapses
   mid-drag (§7.3, §10.1).
   ──────────────────────────────────────────────────────────────────────────────── */

.band {
  position: relative;
  display: block;
  inline-size: 100%;
  min-block-size: calc(var(--drop) + var(--s3));
  touch-action: none;
}

.band[aria-disabled='true'] {
  opacity: 0.4;
}

/*
 * THE SCRUB LOUPE (§7.2). The mono timecode reads 13px and sits at 20px while the
 * thumb is down — as a `transform`, because only transform, opacity and clip-path are
 * ever animated (§7.1) and an animated `font-size` reflows the band under the finger.
 * 20/13 is the ratio; the resting style is the finished style, so reduced motion
 * lands on the readable size with no travel.
 */
.loupe {
  position: absolute;
  inset-block-end: 100%;
  inset-inline-start: var(--played, 0%);
  padding-inline: var(--s1);
  background: var(--bg-panel);
  color: var(--text);
  transform-origin: left bottom;
  transform: scale(1.54);
  animation: loupe var(--t16) var(--ease-in) 1;
  pointer-events: none;
}

@keyframes loupe {
  from {
    transform: scale(1);
  }

  to {
    transform: scale(1.54);
  }
}

/* ────────────────────────────────────────────────────────────────────────────────
   MiniPlayer — §9.1. 56px, docked above the nav strip, carrying the app's one
   licensed blurred shadow (the global `.miniplayer` class in `tokens.css`) because it
   is the one surface that must survive over scrolling content.
   ──────────────────────────────────────────────────────────────────────────────── */

.mini {
  position: relative;
  display: grid;
  /* `[open][toggle][queue?]`. The set-list cell is `auto` like the toggle, so a mini
     player mounted without `onOpenQueue` measures exactly as it did before it existed. */
  grid-template-columns: 1fr auto auto;
  align-items: stretch;
  min-block-size: calc(var(--drop) + var(--s3));
  background: var(--bg-panel);
  border-block-start: 1px solid var(--border-strong);
}

/*
 * Progress is a 1px rule on the bar's own top edge — `transform: scaleX()` from the
 * left, written by the owner every frame. No conic ring, no bar chrome, no second
 * meter: the mini player states progress the way a printed rule states it.
 */
/*
 * The set-list cell. Same 44px target and same 1px rule as `.toggle`, because they are
 * peers: two controls on one strip, divided by the hairline that divides everything
 * else in this application.
 */
.queue {
  display: inline-flex;
  align-items: center;
  justify-content: center;

  /*
   * 56px, not 44px, and a ground of its own — the operator's report of 2026-09-05
   * ("кнопки не очевидны, незаметны"). At `--drop` with `background: none` and
   * `--text-2` ink, the two controls on this strip read as decoration on the panel
   * rather than as things to press: the only thing separating them from the title
   * block was a 1px hairline, and the set-list mark was drawn in the quietest ink the
   * palette has. `--drop` is the MINIMUM touch target (§5.4), not a maximum, so
   * widening is inside the contract; the ground is `--bg-raised`, the same surface
   * every chip in this file already stands on, so no new material is introduced.
   */
  inline-size: calc(var(--drop) + var(--s3));
  border: 0;
  border-inline-start: 1px solid var(--border);
  background: var(--bg-raised);
  color: var(--text);
}

.queue:active {
  background: var(--bg-pressed);
}

.progress {
  position: absolute;
  inset-block-start: 0;
  inset-inline: 0;
  block-size: 1px;
  background: var(--border-strong);
  transform-origin: left center;
}

.progress[data-playing='true'] {
  background: var(--accent);
}

.open {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: var(--s3);
  min-inline-size: 0;
  padding-inline: var(--gutter);
  border: 0;
  background: none;
  color: var(--text);
  text-align: left;
}

.open:active {
  background: var(--bg-pressed);
}

.lines {
  display: grid;
  gap: 0;
  min-inline-size: 0;
}

.miniTitle {
  color: var(--text);
}

.miniArtist {
  color: var(--text-2);
}

/*
 * The play/pause square, divided from the text by a 1px rule (§9.1).
 *
 * 56px and a raised ground since 2026-09-05, for the reason recorded on `.queue`
 * above. The ink is `--accent`: this is the primary action of the whole strip, and the
 * screen it docks over states its own transport in exactly that ink (`Transport`'s
 * PLAY disc). Nothing else on the mini player is accented, so it stays the one loud
 * mark on a quiet bar.
 */
.toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: calc(var(--drop) + var(--s3));
  border: 0;
  border-inline-start: 1px solid var(--border);
  background: var(--bg-raised);
  color: var(--accent);
}

.toggle:active {
  background: var(--bg-pressed);
}

/* ────────────────────────────────────────────────────────────────────────────────
   NavStrip — §9.2. 44px, three items divided by 1px verticals, the active one marked
   by a 2px `--accent` top border and the three-tick ruler glyph. Queue is a sheet,
   not a tab.
   ──────────────────────────────────────────────────────────────────────────────── */

.nav {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  background: var(--bg-panel);
  border-block-start: 1px solid var(--border);
}

.navItem {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--s1);
  min-block-size: var(--drop);
  border: 0;
  border-block-start: 2px solid transparent;
  background: none;
  color: var(--text-3);
}

.navItem + .navItem {
  border-inline-start: 1px solid var(--border);
}

.navItem[aria-current='page'] {
  border-block-start-color: var(--border-active);
  color: var(--text);
}

.navItem:active {
  background: var(--bg-pressed);
}

/* ────────────────────────────────────────────────────────────────────────────────
   TrackRow — §9.3. A ledger row, not a card: 44px chip, one-line title, a mono meta
   line, a tabular duration, and a hairline underneath that steps up to
   `--border-strong` on every fourth row. Those bar lines give the scroll an audible
   metre with zero decoration. ZERO blend modes (§8.2).
   ──────────────────────────────────────────────────────────────────────────────── */

.row {
  display: grid;
  /* `[main][bracket?][more?]` — both trailing cells are `auto`, so a row given neither
     is byte-identical to what it was before the bracket existed. §9.3's 44px targets
     stay 44px; `.main`'s title is `t-clamp-1` and gives up the width. */
  grid-template-columns: 1fr auto auto;
  align-items: stretch;
  min-block-size: calc(var(--drop) + var(--s3));
  border-block-end: 1px solid var(--border);
  border-inline-start: 3px solid transparent;
  background: none;
  list-style: none;
}

/* Bar lines. The `:nth-child` form is the one that survives virtualisation reindexing;
   the explicit class is what a caller that knows its absolute index passes in, and
   what a test with no cascade can read. */
.row:nth-child(4n),
.row.barline {
  border-block-end-color: var(--border-strong);
}

.row[data-state='pending'] {
  border-block-end-style: dashed;
}

.row[data-current='true'] {
  border-inline-start-color: var(--border-active);
}

.main {
  display: grid;
  grid-template-columns: var(--drop) 1fr auto;
  align-items: center;
  gap: var(--s3);
  min-inline-size: 0;
  padding: var(--s1) var(--gutter);
  border: 0;
  background: none;
  color: var(--text);
  text-align: left;
  position: relative;
}

.main:active {
  background: var(--bg-pressed);
}

/*
 * ROW PRESS (§7.2): a 3px `--accent` bar wipes in at the left edge over `--tp`,
 * `transform-origin: bottom`. It is a pseudo-element on the button rather than a
 * border on the row, so it cannot collide with the now-playing rule.
 */
.main::before {
  content: '';
  position: absolute;
  inset-block: 0;
  inset-inline-start: 0;
  inline-size: 3px;
  background: var(--accent);
  transform: scaleY(0);
  transform-origin: bottom;
  transition: transform var(--tp) var(--ease-in);
}

.main:active::before {
  transform: scaleY(1);
}

.rowLines {
  display: grid;
  gap: 0;
  min-inline-size: 0;
}

.rowTitle {
  color: var(--text);
}

.row[data-state='pending'] .rowTitle {
  color: var(--text-2);
}

.rowMeta {
  color: var(--text-3);
}

.duration {
  color: var(--text-2);
}

/* The overflow control is its own button with its own name — never a second meaning
   bolted onto the row (§9.3). */
.more {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--drop);
  border: 0;
  background: none;
  color: var(--text-3);
}

.more:active {
  background: var(--bg-pressed);
}

.moreGlyph {
  display: block;
  inline-size: 3px;
  block-size: var(--s3);
  background-image: repeating-linear-gradient(to bottom, currentcolor 0 3px, transparent 3px 6px);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Transport — §9.4. A faceplate strip divided into cells by 1px rules, with a
   silkscreen label under every control — a strip, not floating circles. Play is the
   exception: a 64px round `--accent` plate with a hard ink offset, overhanging the
   strip's top edge.
   ──────────────────────────────────────────────────────────────────────────────── */

/* The strip and its chip row are one control group, so a screen can place, pad and
   crop them as a single object (§8.1 puts the group last, on the faceplate). */
.faceplateGroup {
  display: grid;
}

.transport {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  min-block-size: calc(var(--drop) + var(--s3));
  padding-block-end: var(--s2);
}

.cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--s1);
  padding-block-start: var(--s2);
}

.cell + .cell {
  border-inline-start: 1px solid var(--border);
}

.skip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--drop);
  block-size: var(--drop);
  border: 0;
  background: none;
  color: var(--text);
}

.skip:active {
  background: var(--bg-pressed);
}

.play {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: var(--bar);
  block-size: var(--bar);
  margin-block-start: calc(var(--s2) * -1);
  border: 0;
  border-radius: var(--r-round);
  background: var(--accent);
  color: var(--text-on-accent);
  box-shadow: var(--ink-offset);
}

/* INKING (§7.2): a control never scales. The ink flips and the glyph knocks out. */
.play:active {
  background: var(--accent-quiet);
}

.secondary {
  display: flex;
  gap: var(--s2);
  align-items: stretch;
  padding: var(--s3) var(--gutter);
  overflow-x: auto;
  scrollbar-width: none;
}

/*
 * A latched transport control is a filter chip with the transport's own ink: solid
 * `--accent` with knocked-out `--text-on-accent` type (§9.4), rather than the filter
 * strip's cream inversion (§9.7). Doubling the class wins the specificity coin-flip
 * against `.chip[data-selected='true']` in the primitives module without `!important`.
 */
.latch.latch {
  block-size: var(--drop);
}

.latch.latch[data-selected='true'] {
  background: var(--accent);
  border-color: var(--accent);
  color: var(--text-on-accent);
}

.latch.latch[disabled] {
  color: var(--text-3);
  background: none;
}

/* ────────────────────────────────────────────────────────────────────────────────
   FlyerDivider — §8.2. Every seventh row, a full-bleed cream flyer at 1.2° with two
   tape strips and a coarse halftone: it breaks the ledger into the *drops* a channel
   actually posts. Hard-cut edges, no shadow, no radius — if it cannot rotate, tape
   and hard-cut, it must be dark instead (§12.14).
   ──────────────────────────────────────────────────────────────────────────────── */

.flyer {
  position: relative;
  display: grid;
  gap: var(--s1);
  padding: var(--s6) var(--gutter);
  background: var(--bg-paper);
  color: var(--text-on-paper);
  clip-path: polygon(0 1.5%, 100% 0, 99.6% 98.5%, 0.4% 100%);
  transform: rotate(1.2deg);
}

.flyerTitle {
  color: var(--text-on-paper);
}

.flyerDate {
  color: var(--text-on-paper);
}

/* The second strip leans the other way, as tape does. */
:global(.paper__tape).tape2 {
  inset-inline-start: 68%;
  transform: rotate(3.5deg);
}

/* ────────────────────────────────────────────────────────────────────────────────
   SpeakerStack — §8.6. Three `--bg-panel` cabinets with 1px keylines and a bass-bin
   cutout arc, the name split one word per panel and each word stretched to fill its
   panel — because that is how real stack lettering works: the word fills the box, so
   the letter widths distort. The loudest typography in the app and the quietest
   colour.
   ──────────────────────────────────────────────────────────────────────────────── */

.stack {
  position: relative;
  display: grid;
  gap: var(--s1);
  isolation: isolate;
}

/* Two overlapping conic gradients at 10% read as horn flare — static, behind, and
   never animated (§6.2's rule for fields). */
.stack::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  background-image:
    conic-gradient(
      from 200deg at 15% 40%,
      var(--accent-quiet) 0deg 40deg,
      transparent 40deg 360deg
    ),
    conic-gradient(from 340deg at 85% 60%, var(--accent-quiet) 0deg 34deg, transparent 34deg 360deg);
  opacity: 0.1;
}

.panel {
  position: relative;
  display: block;
  border: 1px solid var(--border);
  background: var(--bg-panel);
  box-shadow: var(--raised-hi);
}

/* The bass-bin cutout: a radial arc masked out of the cabinet face. */
.panel::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(circle at 50% 120%, var(--bg) 0 38%, transparent 39%);
  mask-image: radial-gradient(circle at 50% 120%, #000 0 40%, transparent 41%);
  -webkit-mask-image: radial-gradient(circle at 50% 120%, #000 0 40%, transparent 41%);
  opacity: 0.55;
}

.word {
  display: block;
  inline-size: 100%;
  fill: var(--text);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The filing bracket — `[ ]`, and never a heart (§8.5, §12.21).
   1px strokes that fill solid accent when the record is filed.
   ──────────────────────────────────────────────────────────────────────────────── */

.bracket {
  display: grid;
  place-items: center;
  inline-size: var(--drop);
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  border-radius: var(--r-round);
  background: transparent;
  cursor: pointer;
}

.bracketMark {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  inline-size: 20px;
  block-size: 20px;
}

.bracketMark::before,
.bracketMark::after {
  content: '';
  display: block;
  border-block: 1px solid var(--text-2);
  transition: background-color var(--t16) var(--ease-cut);
}

.bracketMark::before {
  border-inline-start: 1px solid var(--text-2);
}

.bracketMark::after {
  border-inline-end: 1px solid var(--text-2);
}

.bracket[aria-pressed='true'] .bracketMark::before,
.bracket[aria-pressed='true'] .bracketMark::after {
  border-color: var(--accent);
  background: var(--accent);
}
